const BUSINESS_TIME_ZONE = "America/New_York";

/**
 * `value` is a plain "YYYY-MM-DD" calendar-day string (pto_entry/
 * allowance_period columns) — NOT a timestamp. Parses the components
 * directly and constructs a local-time `Date` rather than round-tripping
 * through `new Date(value)` (which parses a date-only string as UTC
 * midnight): that round trip shows the day BEFORE the stored date once
 * formatted in any timezone behind UTC, including America/New_York. A pure
 * calendar date has no meaningful timezone of its own, so no explicit
 * `timeZone` is passed here — constructing and formatting in the same
 * (ambient) zone is what avoids the drift, not pinning a specific zone.
 */
export function formatDate(value: string | null): string {
  if (!value) return "—";
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * `value` is a real timestamp (TIMESTAMPTZ column, e.g. `createdAt`) — an
 * actual instant, unlike `formatDate`'s calendar-day strings. Pinned to
 * America/New_York so every viewer sees the same business-local time
 * regardless of their own browser's timezone.
 */
export function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    timeZone: BUSINESS_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * A real timestamp (see `formatDateTime`) displayed as just its calendar
 * day — for a "created on" style label where a timestamp is available but
 * only the date matters. Also pinned to America/New_York: the timestamp is
 * an absolute instant, so which calendar day it falls on genuinely depends
 * on the zone (e.g. 11pm UTC is already the next day in UTC but not yet in
 * Eastern) — use this instead of `formatDate` for a `Date`/timestamp value,
 * never for a stored "YYYY-MM-DD" string.
 */
export function formatTimestampDate(value: Date | string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-US", {
    timeZone: BUSINESS_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

/** "0.5 days", "12 days", "1 day" — hours-to-days conversions carry a single
 * decimal at most (see src/lib/pto-balance.ts hoursToDays), never further
 * rounded. */
export function formatDays(value: number): string {
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return `${formatted} day${value === 1 ? "" : "s"}`;
}
